import { Box, Link, Stack, Typography, CircularProgress } from '@mui/joy'
import React from 'react'
import { formatPhoneNumber } from 'utils/load-helpers/helpers'
import { useRTSRating } from 'hooks/useRTSRating'
import { RTSRatingBadge } from './RTSRatingBadge'
import { CompanyColumnProps } from './types'

/**
 * CompanyColumn component displays company information when the load row is expanded.
 * This includes:
 * - Company Name
 * - Contact Info
 * - MC Number
 * - CS/DTP info
 * - Company Location
 */

export const CompanyColumn: React.FC<CompanyColumnProps> = ({ load }) => {
    // Fetch RTS rating data once for the entire component
    const mcNumber = load?.posterInfo?.mcNumber
    const { ratingData, isLoading } = useRTSRating(mcNumber)

    const renderCompanyInfo = () => {
        const { posterInfo } = load
        if (!posterInfo?.contact) return null

        return (
            <>
                <Stack direction={'row'} flexWrap={'wrap'} px={1} justifyContent={'space-between'}>
                    <Stack direction={'column'} gap={0.5}>
                        <Box>
                            {posterInfo.companyName && (
                                <Typography level="body-lg" fontWeight={'bold'}>
                                    {posterInfo.companyName}
                                </Typography>
                            )}
                        </Box>

                        <Box>
                            {posterInfo.contact.email && (
                                <>
                                    <Typography level="body-sm">
                                        <Link href={`mailto:${posterInfo.contact.email}`}>{posterInfo.contact.email}</Link>
                                    </Typography>
                                </>
                            )}
                            {posterInfo.contact.phoneNumber && (
                                <Typography level="body-sm">
                                    <Link href={`tel:${posterInfo.contact.phoneNumber}`}>{formatPhoneNumber(posterInfo.contact.phoneNumber)}</Link>
                                </Typography>
                            )}
                        </Box>
                    </Stack>
                    <Stack direction={'column'} gap={0.5} sx={{ position: 'relative' }}>
                        {posterInfo.credit && <Typography level="body-sm">Credit score: {posterInfo.credit.creditScore}</Typography>}
                        {posterInfo?.credit?.daysToPay && <Typography level="body-sm">Days to pay: {posterInfo.credit.daysToPay}</Typography>}
                    </Stack>
                    <Stack direction={'column'} gap={0.5}>
                        {posterInfo.city && (
                            <Typography level="body-sm">
                                {posterInfo.city}, {posterInfo.carrierHomeState}{' '}
                            </Typography>
                        )}
                        {posterInfo.mcNumber && (
                            <Stack direction="column" spacing={0.5}>
                                <Typography level="body-sm">MC#{posterInfo.mcNumber}</Typography>
                                <Stack direction="column" spacing={0.5} sx={{ mt: 1 }}>
                                    <Box sx={{ position: 'relative' }}>
                                        <Typography level="body-sm" fontWeight="medium" sx={{ color: 'text.primary' }}>
                                            Factoring check:
                                        </Typography>
                                        <Box>
                                            {
                                                <Box sx={{ display: 'flex', alignItems: 'center', borderRadius: '4px', px: 0, py: 0.5, width: 'fit-content' }}>
                                                    {isLoading ? (
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <CircularProgress size="sm" />
                                                            <Typography level="body-md" sx={{ color: 'text.primary', fontWeight: 'medium' }}>
                                                                Loading RTS Rating...
                                                            </Typography>
                                                        </Box>
                                                    ) : (
                                                        <>
                                                            {ratingData && (
                                                                <>
                                                                    <RTSRatingBadge
                                                                        rating={ratingData.rating}
                                                                        mcNumber={posterInfo.mcNumber}
                                                                        ratingData={ratingData}
                                                                    />
                                                                    <Typography level="body-md" sx={{ ml: 1, color: 'text.primary', fontWeight: 'medium' }}>
                                                                        RTS Rating: {ratingData.rating}
                                                                    </Typography>
                                                                </>
                                                            )}
                                                        </>
                                                    )}
                                                </Box>
                                            }
                                        </Box>
                                    </Box>
                                </Stack>
                            </Stack>
                        )}
                    </Stack>
                </Stack>
            </>
        )
    }

    return (
        <>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    width: '100%',
                }}
            >
                {/* Header */}
                <Typography
                    sx={{
                        color: 'text.secondary',
                        backgroundColor: 'background.surface',
                        px: 1,
                        fontWeight: 'bold',
                    }}
                >
                    Company
                </Typography>

                {renderCompanyInfo()}
            </Box>
        </>
    )
}
